// services/StudentService.js
import {
  createDocument,
  updateDocument,
  deleteDocument,
  getDocument,
  getCollection,
  getCollectionQuery,
} from "./FirebaseService";
import { getDocs, collection, query, orderBy, startAt, endAt, where,
  limit as limitFn } from "firebase/firestore";
import { db } from "../auth/firebase";

const COLLECTION = "students";

/**
 * Map raw data to Student model
 */
export const mapFormStateToStudent = (state) => ({
  cietStudentId: state.personal?.studentId || "",
  previouslyStudiedAtCIET: state.personal?.previouslyStudiedAtCIET || "No",
  usiNumber: state.personal?.usiNumber || "",
  title: state.personal?.title || "",
  givenNames: state.personal?.givenNames || "",
  familyName: state.personal?.familyName || "",
  dateOfBirth: state.personal?.dateOfBirth || "",
  gender: state.personal?.gender || "",
  countryOfBirth: state.personal?.countryOfBirth || "",
  citizenship: state.personal?.citizenship || "",
  passportNumber: state.personal?.passportNumber || "",
  passportExpiry: state.personal?.passportExpiry || "",
  passportCountryOfIssue: state.personal?.passportCountryOfIssue || "",
  inAustraliaAtApplication: state.personal?.inAustraliaAtApplication || "No",
  yearOfArrival: state.personal?.yearOfArrival || "",
  maritalStatus: state.personal?.maritalStatus || "",
  address: state.personal?.address || "",
  suburb: state.personal?.suburb || "",
  state: state.personal?.state || "",
  country: state.personal?.country || "",
  postcode: state.personal?.postcode || "",
  mobilePhone: state.personal?.mobilePhone || "",
  telephone: state.personal?.telephone || "",
  email: state.personal?.email || "",
  permanentHomeAddress: state.personal?.permanentHomeAddress || "",
  intake: {
    month: state.intake?.month || "",
    year: state.intake?.year || ""
  },
  agentId: state.personal?.agent?.id || null,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
});
const mapStudent = (state, enrolmentReferenceId) => ({
  cietStudentId:
    state?.cietStudentId ||
    state?.studentId ||
    state?.personal?.cietStudentId ||
    state?.personal?.studentId ||
    "",
  enrolmentReferenceId: enrolmentReferenceId || "",
  formState: state || {},
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});
const mapFormStateToUpdateStudent = (id, state, enrolmentReferenceId) => ({
  studentId: id || "",
  cietStudentId:
    state?.cietStudentId ||
    state?.studentId ||
    state?.personal?.cietStudentId ||
    state?.personal?.studentId ||
    "",
  enrolmentReferenceId: enrolmentReferenceId || "",
  formState: state || {},
  updatedAt: new Date().toISOString(),
});
const mapFollowUpsToStudent = (state, enrolmentReferenceId, followUps) => ({
  studentId: state.personal?.studentId || "",        // replace with your internal mapping
  cietStudentId: state.personal?.cietStudentId || "",
  enrolmentReferenceId: enrolmentReferenceId || "",
  formState: state || {},
  updatedAt: new Date().toISOString(),
  followUps: followUps || [],
});
const mapFeesPlanToStudent = (state, enrolmentReferenceId) => ({
  studentId: state.personal?.studentId || "",       
  cietStudentId: state.personal?.cietStudentId || "",
  enrolmentReferenceId: enrolmentReferenceId || "",
  formState: state || {},
  updatedAt: new Date().toISOString(),
  // [courseName || "unknownCourse"]: feesPlan || {},
});
const mapFormStateToOfferLetter = (state, offerLetterApplication) => {
  const studentFormState = state?.formState || {};

  return {
    studentId: state?.studentId || studentFormState?.studentId || "",
    cietStudentId:
      studentFormState?.cietStudentId ||
      studentFormState?.studentId ||
      studentFormState?.personal?.cietStudentId ||
      studentFormState?.personal?.studentId ||
      "",
    enrolmentReferenceId: state?.enrolmentReferenceId || "",
    formState: studentFormState,
    updatedAt: new Date().toISOString(),
    offerLetterApplication: offerLetterApplication || {},
  };
};

// CRUD
export const createStudent = async (data, id) => {
  const student = mapFormStateToStudent(data);
  return await createDocument(COLLECTION, student, id);
};
export const createStateStudent = async (data, enrolmentReferenceId, id) => {
  const response = await createDocument(COLLECTION, mapStudent(data, enrolmentReferenceId), id );
  return response || "";
};


export const updateStudent = async (id, enrolmentReferenceId, data) => {
  const student = mapFormStateToUpdateStudent(id, data, enrolmentReferenceId);
  return await updateDocument(COLLECTION, id, student);
};

export const deleteStudent = async (id) => {
  return await deleteDocument(COLLECTION, id);
};

export const getStudent = async (id) => {
  return await getDocument(COLLECTION, id);
};
export const updateStudentFollowUps = async (id, state, enrolmentReferenceId, followUps) => {
  const student = mapFollowUpsToStudent(state, enrolmentReferenceId, followUps);
  return await updateDocument(COLLECTION, id, student);
};
export const updateStudentFeesPlan = async( id, state, enrolmentReferenceId) => {
  const student = mapFeesPlanToStudent(state, enrolmentReferenceId);
  return await updateDocument(COLLECTION, id, student);
};

export const updateStudentOfferLetter = async (id, data, offerLetterApplication) =>
  await updateDocument(COLLECTION, id, mapFormStateToOfferLetter(data, offerLetterApplication));

export const getAllStudents = async (searchText = "") => {
  const studentsRef = collection(db, COLLECTION);

  // No search -> return everything (you may want to page this in real app)
  if (!searchText || !searchText.trim()) {
    const snapshot = await getDocs(studentsRef);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  }

  const s = searchText.trim().toLowerCase();
  const end = s + "\uf8ff";

  // Quick check: do docs already contain lowercased search fields?
  try {
    const sampleSnap = await getDocs(query(studentsRef, orderBy("__name__"), limitFn(1)));
    const sampleDoc = sampleSnap.docs[0];
    const sampleData = sampleDoc ? sampleDoc.data() : null;

    const hasLowerFields =
      sampleData &&
      (Object.prototype.hasOwnProperty.call(sampleData, "studentId_lower") ||
        Object.prototype.hasOwnProperty.call(sampleData, "firstName_lower") ||
        Object.prototype.hasOwnProperty.call(sampleData, "lastName_lower") ||
        Object.prototype.hasOwnProperty.call(sampleData, "fullName_lower"));

    if (hasLowerFields) {
      // run prefix queries in parallel on the lowercased fields
      const fieldsToQuery = ["studentId_lower", "firstName_lower", "lastName_lower", "fullName_lower"];
      const queries = fieldsToQuery.map((field) =>
        query(studentsRef, orderBy(field), startAt(s), endAt(end))
      );

      const snaps = await Promise.all(queries.map((q) => getDocs(q).catch(() => ({ docs: [] }))));

      const results = [];
      snaps.forEach((snap) =>
        snap.docs.forEach((doc) => {
          results.push({ id: doc.id, ...doc.data() });
        })
      );

      // dedupe
      const unique = Object.values(results.reduce((acc, cur) => {
        acc[cur.id] = acc[cur.id] || cur;
        return acc;
      }, {}));

      return unique;
    } else {
      // FALLBACK: no lowercased fields -> client-side filter (inefficient for large collections)
      const allSnap = await getDocs(studentsRef);
      const all = allSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const filtered = all.filter((st) => {
        const fs = st.formState || st; // in your code student data is nested in formState
        const studentId = (fs.studentId || "").toString().toLowerCase();
        const firstName = (fs.firstName || "").toLowerCase();
        const lastName = (fs.lastName || "").toLowerCase();
        const fullName = `${fs.firstName || ""} ${fs.lastName || ""}`.trim().toLowerCase();

        return (
          studentId.includes(s) ||
          firstName.includes(s) ||
          lastName.includes(s) ||
          fullName.includes(s)
        );
      });

      return filtered;
    }
  } catch (err) {
    console.error("getAllStudents error", err);
    // As a last fallback, return empty array
    return [];
  }
};


/**
 * Fetch students from collection with optional search and pagination
 * @param {Object} options
 *   options.limit {number} - number of records per page
 *   options.startAfter {string} - timestamp/id to start after (for pagination)
 *   options.searchText {string} - search text to filter studentId, firstName, lastName
 */
export const getStudentsPage = async ({ limit = 10, startAfter = null, searchText = "" } = {}) => {
  // Build Firestore query
  const queryOptions = {
    collectionName: COLLECTION,
    orderBy: "createdAt",
    limit,
    startAfter, // for pagination
  };

  if (searchText) {
    queryOptions.search = {
      fields: ["formState.studentId", "formState.firstName", "formState.lastName"],
      value: searchText,
    };
  }

  const result = await getCollectionQuery(queryOptions);
  return result;
};

export const getNewEnrolmentCount = async () => {
  try {
    const q = query(
      collection(db, COLLECTION),
      where("formState.studentStatus", "==", "New Enrolment")
    );
    const snapshot = await getDocs(q);
    return snapshot.size; // number of new enrolments
  } catch (error) {
    console.error("Failed to fetch new enrolment count:", error);
    return 0;
  }
};

/**
 * Update a student's contactID in their formState
 * @param {string} id - The student document ID
 * @param {string} contactID - The Axcelerate contact ID to set
 * @returns {Promise<{success: boolean, contactID?: string, error?: string}>}
 */
export const updateStudentContactID = async (id, contactID) => {
  try {
    const student = await getDocument(COLLECTION, id);
    if (!student) {
      throw new Error("Student not found");
    }

    const updatedFormState = {
      ...student.formState,
      contactID: contactID
    };

    await updateDocument(COLLECTION, id, {
      formState: updatedFormState,
      updatedAt: new Date().toISOString()
    });

    return { success: true, contactID };
  } catch (error) {
    console.error("Failed to update student contactID:", error);
    return { success: false, error: error.message };
  }
};